/**
 * AdminActivity.tsx — Activity view (read-only).
 *
 * Fetches /api/admin/activity?limit=2000, aggregates per email client-side,
 * renders a summary table and a recent-events detail list.
 *
 * Rules followed (mirrors AdminPartners):
 * - Sticky forbidden state: once ANY call returns 403 the state never reverts.
 * - All server strings rendered as React text. NO dangerouslySetInnerHTML.
 * - No mutations (read-only endpoint).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch, type SerializedActivity } from '../lib/adminApi';

// ─── Types ──────────────────────────────────────────────────────────────────

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'forbidden' }
  | { phase: 'ready'; events: SerializedActivity[] };

interface EmailSummary {
  email: string;
  isStaff: boolean;
  logins: number;
  pageviews: number;
  pages: number;          // distinct paths viewed
  totalDwellMs: number;
  lastSeenTs: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Format milliseconds as "Xh Ym", "Ym Zs", or "Zs".
 * Drops leading zero units; zero ms → "0s".
 */
function fmtDwell(ms: number): string {
  if (ms <= 0) return '0s';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * Relative time from an ISO string, e.g. "3h ago", "2m ago", "just now".
 * Returns null if ts is null or unparseable.
 */
function relativeTime(ts: string | null): string | null {
  if (!ts) return null;
  try {
    const diffMs = Date.now() - new Date(ts).getTime();
    if (!Number.isFinite(diffMs)) return null;
    const abs = Math.abs(diffMs);
    if (abs < 60_000) return 'just now';
    if (abs < 3_600_000) {
      const m = Math.round(abs / 60_000);
      return `${m}m ago`;
    }
    if (abs < 86_400_000) {
      const h = Math.round(abs / 3_600_000);
      return `${h}h ago`;
    }
    const d = Math.round(abs / 86_400_000);
    return `${d}d ago`;
  } catch {
    return null;
  }
}

// ─── Aggregation ─────────────────────────────────────────────────────────────

function aggregateByEmail(events: SerializedActivity[]): EmailSummary[] {
  const map = new Map<string, EmailSummary>();

  for (const ev of events) {
    if (!map.has(ev.email)) {
      map.set(ev.email, {
        email: ev.email,
        isStaff: ev.isStaff,
        logins: 0,
        pageviews: 0,
        pages: 0,
        totalDwellMs: 0,
        lastSeenTs: null,
      });
    }
    const row = map.get(ev.email)!;

    // Update isStaff: if any event says staff, mark as staff.
    if (ev.isStaff) row.isStaff = true;

    // Update last seen: keep the max ts.
    if (ev.ts) {
      if (!row.lastSeenTs || ev.ts > row.lastSeenTs) {
        row.lastSeenTs = ev.ts;
      }
    }

    if (ev.type === 'login') {
      row.logins += 1;
    } else if (ev.type === 'pageview') {
      row.pageviews += 1;
    } else if (ev.type === 'dwell') {
      row.totalDwellMs += ev.dwellMs ?? 0;
    }
  }

  // Compute distinct pages per email (distinct non-null path among pageviews).
  const pathSets = new Map<string, Set<string>>();
  for (const ev of events) {
    if (ev.type === 'pageview' && ev.path) {
      if (!pathSets.has(ev.email)) pathSets.set(ev.email, new Set());
      pathSets.get(ev.email)!.add(ev.path);
    }
  }
  for (const [email, paths] of pathSets) {
    const row = map.get(email);
    if (row) row.pages = paths.size;
  }

  // Sort by last-seen desc; null last-seen sorts last.
  return [...map.values()].sort((a, b) => {
    const ta = a.lastSeenTs ?? '';
    const tb = b.lastSeenTs ?? '';
    return tb.localeCompare(ta);
  });
}

// ─── RoleBadge ───────────────────────────────────────────────────────────────

function RoleBadge({ isStaff }: { isStaff: boolean }) {
  if (isStaff) {
    return <span className="admin__badge admin__badge--staff">Staff</span>;
  }
  return <span className="admin__badge admin__badge--partner">Partner</span>;
}

// ─── SummaryTable ─────────────────────────────────────────────────────────────

function SummaryTable({ rows }: { rows: EmailSummary[] }) {
  if (rows.length === 0) {
    return <p className="admin__empty">No activity recorded yet.</p>;
  }

  return (
    <div className="admin__table-wrap">
      <table className="admin__table" aria-label="Activity summary by partner">
        <thead>
          <tr>
            <th className="admin__th">Partner</th>
            <th className="admin__th">Role</th>
            <th className="admin__th admin__th--num">Logins</th>
            <th className="admin__th admin__th--num">Pageviews</th>
            <th className="admin__th admin__th--num">Pages</th>
            <th className="admin__th admin__th--num">Dwell</th>
            <th className="admin__th">Last seen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rel = relativeTime(row.lastSeenTs);
            return (
              <tr
                key={row.email}
                className={`admin__tr${row.isStaff ? ' admin__tr--staff' : ''}`}
              >
                <td className="admin__td admin__td--value">
                  <span className="admin__mono admin__activity-email">{row.email}</span>
                </td>
                <td className="admin__td">
                  <RoleBadge isStaff={row.isStaff} />
                </td>
                <td className="admin__td admin__td--num">{row.logins}</td>
                <td className="admin__td admin__td--num">{row.pageviews}</td>
                <td className="admin__td admin__td--num">{row.pages}</td>
                <td className="admin__td admin__td--num">
                  {row.totalDwellMs > 0 ? fmtDwell(row.totalDwellMs) : (
                    <span className="admin__muted">—</span>
                  )}
                </td>
                <td className="admin__td">
                  {rel ? (
                    <span title={row.lastSeenTs ?? undefined}>{rel}</span>
                  ) : (
                    <span className="admin__muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── RecentEvents ─────────────────────────────────────────────────────────────

const RECENT_LIMIT = 50;

function RecentEvents({ events }: { events: SerializedActivity[] }) {
  const recent = events.slice(0, RECENT_LIMIT);

  if (recent.length === 0) {
    return <p className="admin__empty">No events yet.</p>;
  }

  return (
    <ul className="admin__activity-events" aria-label="Recent events">
      {recent.map((ev) => {
        const rel = relativeTime(ev.ts);
        const isPageview = ev.type === 'pageview';
        const isDwell = ev.type === 'dwell';
        return (
          <li key={ev.id} className="admin__activity-event">
            <span
              className="admin__activity-time"
              title={ev.ts ?? undefined}
            >
              {rel ?? '—'}
            </span>
            <span className={`admin__activity-type admin__activity-type--${ev.type}`}>
              {ev.type}
            </span>
            <span className="admin__mono admin__activity-email">{ev.email}</span>
            {(isPageview || isDwell) && ev.path ? (
              <span className="admin__mono admin__activity-path">{ev.path}</span>
            ) : null}
            {isDwell && ev.dwellMs != null ? (
              <span className="admin__activity-dwell">{fmtDwell(ev.dwellMs)}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

// ─── AdminActivity (main export) ─────────────────────────────────────────────

export default function AdminActivity() {
  const { getIdToken } = useAuth();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [reloadTick, setReloadTick] = useState(0);
  // Sticky forbidden: once 403 is received, no subsequent result overwrites it.
  const forbiddenRef = useRef(false);

  const triggerReload = useCallback(() => {
    if (forbiddenRef.current) return;
    setReloadTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (forbiddenRef.current) return;
    let cancelled = false;
    setState({ phase: 'loading' });

    (async () => {
      const result = await adminFetch(getIdToken, 'GET', '/api/admin/activity?limit=2000');
      if (cancelled || forbiddenRef.current) return;

      if (result.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        return;
      }
      if (!result.ok) {
        const msg =
          result.status === 0
            ? (result.data?.message ?? 'Network error — check your connection.')
            : `Failed to load (HTTP ${result.status}).`;
        setState({ phase: 'error', message: msg });
        return;
      }

      const events: SerializedActivity[] = Array.isArray(result.data?.events)
        ? (result.data.events as SerializedActivity[])
        : [];
      setState({ phase: 'ready', events });
    })();

    return () => { cancelled = true; };
  // reloadTick drives manual refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIdToken, reloadTick]);

  // ── Forbidden ──────────────────────────────────────────────────────────────
  if (state.phase === 'forbidden') {
    return (
      <div className="admin__forbidden" role="alert">
        <p className="admin__forbidden-title">Session no longer authorized</p>
        <p className="admin__forbidden-body">
          Your session is no longer authorized (staff access may have changed). Sign in again.
        </p>
      </div>
    );
  }

  // ── Aggregate (only when ready) ────────────────────────────────────────────
  const summaryRows =
    state.phase === 'ready' ? aggregateByEmail(state.events) : [];

  return (
    <section className="admin__section" aria-label="Activity">
      <header className="admin__section-head">
        <h2 className="admin__section-title">Activity</h2>
        <p className="admin__section-sub">
          Partner and staff engagement — logins, page visits, and time on page.
        </p>
      </header>

      {/* Summary table card */}
      <div className="admin__card admin__card--table">
        <h3 className="admin__card-title">
          Summary by partner
          {state.phase === 'ready' && (
            <span className="admin__count">{summaryRows.length}</span>
          )}
          <button
            type="button"
            className="admin__btn admin__btn--sm admin__btn--secondary admin__activity-refresh"
            onClick={triggerReload}
            disabled={state.phase === 'loading'}
            aria-label="Refresh activity"
          >
            {state.phase === 'loading' ? 'Loading…' : 'Refresh'}
          </button>
        </h3>

        {state.phase === 'loading' && (
          <p className="admin__loading" aria-live="polite">Loading…</p>
        )}

        {state.phase === 'error' && (
          <div className="admin__list-error" role="alert">
            <p>{state.message}</p>
            <button
              type="button"
              className="admin__btn admin__btn--secondary"
              onClick={triggerReload}
            >
              Retry
            </button>
          </div>
        )}

        {state.phase === 'ready' && (
          <SummaryTable rows={summaryRows} />
        )}
      </div>

      {/* Recent events card */}
      {state.phase === 'ready' && (
        <div className="admin__card">
          <h3 className="admin__card-title">
            Recent events
            <span className="admin__count">
              {Math.min(state.events.length, RECENT_LIMIT)}
            </span>
          </h3>
          <p className="admin__activity-caveat">
            Dwell is wall-clock time on a page; idle time with the tab focused inflates it.
          </p>
          <RecentEvents events={state.events} />
        </div>
      )}
    </section>
  );
}
